import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import type { Transaction, EditorState } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { ETiptapAlignmentSelectorKey } from '../../../../types/tiptap.types';
import isEmptyParagraph from '../../../utils/isEmptyParagraph';
import { ELEMENT_ROW_NODE_NAME } from '../constants';

export type BlockInfo = {
  pos: number; // absolute position *before* the node
  node: PMNode; // the block node
  depth: number; // its depth
  parentPos: number; // pos before its parent
  parent: any; // parent node
};

export function getActiveBlock(state: EditorState): BlockInfo | null {
  const { selection } = state;

  if (selection instanceof NodeSelection && selection.node.isBlock) {
    const pos = selection.from;
    const $pos = state.doc.resolve(pos);
    const depth = $pos.depth;
    return {
      pos,
      node: selection.node,
      depth,
      parentPos: depth ? $pos.before(depth) : 0,
      parent: $pos.node(depth - 1),
    };
  }

  // Use the block that contains the cursor/selection head
  const $from = selection.$from;
  let d = $from.depth;
  while (d > 0 && !$from.node(d).isBlock) d--;
  if (d <= 0) return null;

  const node = $from.node(d);
  const pos = $from.before(d);
  return { pos, node, depth: d, parent: node, parentPos: pos };
}

export function findParentRow(state: EditorState, absPos: number) {
  const rowType = state.schema.nodes[ELEMENT_ROW_NODE_NAME];
  const $pos = state.doc.resolve(absPos);
  for (let d = $pos.depth; d >= 0; d--) {
    const n = $pos.node(d);
    if (n.type === rowType) {
      const start = d ? $pos.before(d) : 0;
      return { node: n, pos: start, depth: d };
    }
  }
  return null;
}

export function isLastChildOf(parentNode: PMNode, childAbsPos: number, parentAbsPos: number): boolean {
  // child absolute start -> offset within parent content
  const offset = childAbsPos - (parentAbsPos + 1);
  let acc = 0,
    idx = 0;
  for (let i = 0; i < parentNode.childCount; i++) {
    const c = parentNode.child(i);
    if (acc === offset) {
      idx = i;
      break;
    }
    acc += c.nodeSize;
  }
  return idx === parentNode.childCount - 1;
}

export function getNextSibling(state: EditorState, absPos: number, nodeSize: number) {
  const nextPos = absPos + nodeSize;
  const next = state.doc.nodeAt(nextPos);
  return { node: next, pos: nextPos };
}

/**
 * Returns the first non-empty block node at/after `fromPos`.
 * - Skips empty paragraphs.
 * - Returns rows as-is (so callers can merge).
 */
export function findFirstNonEmptyAfter(state: EditorState, fromPos: number) {
  const { doc } = state;
  let pos = fromPos;

  while (pos <= doc.content.size) {
    const $pos = doc.resolve(pos);
    const node = $pos.nodeAfter;
    if (!node) return null;

    // Skip empty paras, otherwise return any block (including element-row-node)
    if (!isEmptyParagraph(node)) {
      return { node, pos };
    }

    pos += node.nodeSize;
  }

  return null;
}

/** Insert content at the end of a row; returns { tr, insertedPos }. */
export function insertAtEndOfRow(tr: Transaction, rowPos: number, content: PMNode) {
  const row = tr.doc.nodeAt(rowPos);
  if (!row) return { tr, insertedPos: null };
  const insertPos = rowPos + row.nodeSize - 1; // before row's closing
  tr = tr.insert(insertPos, content);
  return { tr, insertedPos: insertPos };
}

/** Place caret at the start of the given block node. */
export function selectAtBlockStart(tr: Transaction, startPos: number, hint?: PMNode | null): Transaction {
  const { doc } = tr;

  let at = startPos;
  let node: PMNode | null | undefined = hint ?? doc.nodeAt(startPos);
  const $pos = doc.resolve(startPos);

  // Fallback: sometimes nodeAt(startPos) is null after complex inserts/merges.
  if (!node) {
    if ($pos.nodeAfter) {
      node = $pos.nodeAfter; // starts exactly at startPos
      at = startPos;
    } else if ($pos.nodeBefore) {
      node = $pos.nodeBefore; // compute the node’s true start
      at = startPos - node.nodeSize;
    }
  }

  if (!node) return tr; // nothing reasonable - leave selection as-is

  // Place a text cursor for paragraphs/headings; node selection for atom blocks (images, embeds, etc.)
  return node.isTextblock
    ? tr.setSelection(TextSelection.create(doc, at + 1))
    : tr.setSelection(NodeSelection.create(doc, at));
}

export function getBlockAlign(
  node?: PMNode | null,
  fallback: ETiptapAlignmentSelectorKey = ETiptapAlignmentSelectorKey.LEFT
): ETiptapAlignmentSelectorKey {
  const v = (node?.attrs as any)?.textAlign;
  return v === ETiptapAlignmentSelectorKey.CENTER || v === ETiptapAlignmentSelectorKey.RIGHT
    ? v
    : ETiptapAlignmentSelectorKey.LEFT;
}
